// src/polaris-components/SubscriptionSuspendedModal.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isAdmin } from '../utils/auth';
import '../styles/subscription-suspended-modal.css';

interface SubscriptionSuspendedModalProps {
  isOpen: boolean;
}

/**
 * Blocking modal displayed when user's subscription is suspended
 * Prevents access to main features until payment is resolved
 */
export const SubscriptionSuspendedModal: React.FC<SubscriptionSuspendedModalProps> = ({
  isOpen
}) => {
  const navigate = useNavigate();
  const userIsAdmin = isAdmin();

  const handleUpdatePayment = () => {
    navigate('/billing');
  };

  if (!isOpen) return null;

  return (
    <div
      className="subscription-suspended-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="suspended-modal-title"
      data-testid="subscription-suspended-modal"
    >
      <div className="subscription-suspended-modal__content">
        <div className="subscription-suspended-modal__icon">
          <svg
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle
              cx="32"
              cy="32"
              r="30"
              stroke="currentColor"
              strokeWidth="3"
            />
            <path
              d="M32 16V32"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <circle
              cx="32"
              cy="44"
              r="2"
              fill="currentColor"
            />
          </svg>
        </div>

        <h2
          id="suspended-modal-title"
          className="subscription-suspended-modal__title"
        >
          Subscription Suspended
        </h2>

        <p className="subscription-suspended-modal__message">
          {userIsAdmin
            ? 'Your subscription has been suspended due to a payment failure. Please update your payment method to restore access to your account.'
            : 'Your subscription has been suspended due to a payment failure. Please contact your account administrator to restore access.'}
        </p>

        {userIsAdmin && (
          <div className="subscription-suspended-modal__details">
            <h3>Why was my subscription suspended?</h3>
            <p>
              We were unable to process your payment after multiple attempts.
              This may be due to:
            </p>
            <ul>
              <li>Insufficient funds</li>
              <li>Expired or invalid card</li>
              <li>Card declined by your bank</li>
            </ul>
          </div>
        )}

        <div className="subscription-suspended-modal__actions">
          {userIsAdmin && (
            <button
              className="subscription-suspended-modal__primary-button"
              onClick={handleUpdatePayment}
              data-testid="suspended-update-payment-button"
              autoFocus
            >
              Update Payment Method
            </button>
          )}

          <p className="subscription-suspended-modal__help">
            Need help? Contact support at{' '}
            <a href="mailto:support@polarisathlete.com">
              support@polarisathlete.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

